// Hints on a streamed Mac window, the web's half (docs/features/stream-hints.md; the headset's StreamHints/): a Hints
// button in the ornament; while on, a label (letters, or a zone's number) on everything the Mac found that can be
// pressed — always at full strength, the cap and the zones the only clutter control — and one small row under the
// picture: the mic (on-device speech only, hints-voice.js), the label field (it appears with Hints, never takes the
// keyboard by itself), what was heard a moment. Say a label, type it, or tap its badge. The Mac decides what words
// mean (HintsDesk): this side sends `heard`, `press`, `get`, `approve`, `off` and draws what comes back.
//
// One seam, used by macwindow.js: `new StreamHints(win)` → `button()`, `overlay()` (inside the picture), `act(kind,
// arg)` (undefined when a tap isn't its), `typed(key, value)`, `submit(key, value)`, `hold(kind, arg)`, `dispose()`.
// The stream (player.js) gives `hintsSend(message)` and calls `onHints(message)`.
//
// Live (stream-hints.md › Live): while Hints show, the Mac follows the window and sends only what changed — a `diff`
// {base, version, hints (moved or new), gone} on the list held. It is applied in place: each badge stays the same
// node, moved by its style, new ones added, gone ones removed — no redraw, no flicker. A diff that doesn't start
// from the list held asks for the whole list again.
import { esc, icon } from '../../core/kit.js'
import { localSpeech, listen } from './hints-voice.js'

const img = (symbol, colour = 'var(--c-ink)') => `<img class="sym" src="${icon(symbol, colour)}">`
const pct = (v) => `${Math.round(Math.min(Math.max(v, 0), 1) * 10000) / 100}%`
const badge = (h) => `<button class="hint" data-act="hint:${esc(h.label)}" style="left:${pct(h.x)};top:${pct(h.y)}"${h.title ? ` data-help="${esc(h.title)}"` : ''}>${esc(h.label.toUpperCase())}</button>`

/// A `list` or a `diff` applied to the list held (the Swift twin: HintsMessage.applied): the new list, or null when a
/// diff doesn't start from it. Labels keep their place; a moved one is replaced where it stands, a new one goes last.
export function applyHints(held, m) {
  if (m?.what === 'list') return m
  if (m?.what !== 'diff' || !held || held.what !== 'list' || held.version !== m.base) return null
  const gone = new Set(m.gone || []), fresh = new Map((m.hints || []).map((h) => [h.label, h]))
  const hints = (held.hints || []).filter((h) => !gone.has(h.label)).map((h) => fresh.get(h.label) || h)
  const kept = new Set(hints.map((h) => h.label))
  for (const h of m.hints || []) if (!kept.has(h.label)) hints.push(h)
  return { ...held, hints, version: m.version }
}

export class StreamHints {
  constructor(win) {
    this.win = win
    this.on = false
    this.list = null        // the Mac's last `list`
    this.flash = null       // shown a moment: heard and not used, done, or why not
    this.ask = null         // {id, say}: a command that asks first
    this.voice = null       // the listener while it runs
    this.listening = false
    this.voiceProblem = null
    if (this.stream) this.stream.onHints = (m) => this.receive(m)
  }
  get stream() { return this.win.stream }
  send(m) { this.stream?.hintsSend?.(m) }
  render() { this.win.render() }

  // MARK: drawing

  button() {
    if (!this.stream?.hintsSend) return ''
    return `<button class="ob${this.on ? ' on' : ''}" data-act="hints:" data-help="Labels on what can be pressed: say one, type it, or tap it">${img('character.cursor.ibeam', this.on ? 'var(--c-control-on-ink)' : 'var(--c-ink)')}<span>Hints</span></button>`
  }

  overlay() {
    if (!this.on) return ''
    const m = this.list || {}
    const zones = (m.zones || []).map((z) => `<button class="hzone" data-act="hzone:${esc(z.label)}" style="left:${pct(z.x)};top:${pct(z.y)};width:${pct(z.w)};height:${pct(z.h)}"><span class="hzl">${esc(z.label)}</span></button>`).join('')
    const hints = (m.hints || []).map(badge).join('')
    const words = this.flash || this.voiceProblem
    const mic = this.listening
      ? `<button class="ob icon on" data-act="hmic:" aria-label="Stop listening">${img('waveform', 'var(--c-control-on-ink)')}</button>`
      : `<button class="ob icon" data-act="hmic:" aria-label="Listen">${img('mic')}</button>`
    const ask = this.ask ? `<span class="hask">${esc(this.ask.say)}</span><button class="ob" data-act="happrove:">Do It</button><button class="ob" data-act="hcancel:">Cancel</button>` : ''
    const bar = `<div class="hbar">${ask}${mic}<input class="hfield" data-key="hint" placeholder="Label" autocomplete="off" autocapitalize="off" spellcheck="false" size="6">${words ? `<span class="hheard">${esc(words)}</span>` : ''}</div>`
    return `<div class="hlayer">${zones}${hints}</div>${bar}`
  }

  // MARK: taps and typing

  /// A tap: undefined when it isn't Hints'; false (drawn already) or null (draw again) when it is.
  act(kind, arg) {
    if (kind === 'hints') { this.on ? this.turnOff() : this.turnOn(true); return false }
    if (!this.on) return undefined
    if (kind === 'hint') { this.press(arg); return false }
    if (kind === 'hzone') { this.send({ what: 'get', zone: arg }); return false }
    if (kind === 'hmic') { this.listening ? this.stopVoice() : this.startVoice(); return null }
    if (kind === 'happrove') { if (this.ask) { this.send({ what: 'approve', ask: this.ask.id }); this.ask = null }; return null }
    if (kind === 'hcancel') { if (this.ask) { this.send({ what: 'heard', text: 'cancel' }); this.ask = null }; return null }
    return undefined
  }

  /// A long press on a badge: its element's title, a moment.
  hold(kind, arg) {
    if (kind !== 'hint') return undefined
    const t = this.list?.hints?.find((h) => h.label === arg)?.title
    if (t) this.show(t)
    return true
  }

  /// Typing in the label field: a whole label (or zone) presses at once — labels never begin one another.
  typed(key, value) {
    if (key !== 'hint') return false
    const t = String(value || '').trim().toLowerCase()
    const field = this.win.root?.querySelector?.('.hfield')
    const clear = () => { if (field) field.value = '' }
    if (this.list?.hints?.some((h) => h.label === t)) { clear(); this.press(t) }
    else if (this.list?.zones?.some((z) => z.label === t)) { clear(); this.send({ what: 'get', zone: t }) }
    return true
  }

  /// Return in the field: anything else as words (a command, "type …", "back").
  submit(key, value) {
    if (key !== 'hint') return false
    const t = String(value || '').trim()
    const field = this.win.root?.querySelector?.('.hfield')
    if (field) field.value = ''
    if (t) this.send({ what: 'heard', text: t })
    return true
  }

  press(label) { this.send({ what: 'press', label, version: this.list?.version }) }

  // MARK: on and off

  turnOn(tapped = false) {
    if (this.on) return
    this.on = true
    this.flash = null
    this.send({ what: 'get' })
    if (tapped) this.startVoice()   // the tap is the person's: the mic may start (never from the Mac's "show")
    this.render()
  }

  turnOff(tellMac = true) {
    if (!this.on) return
    this.on = false
    this.list = null; this.ask = null
    this.stopVoice()
    if (tellMac) this.send({ what: 'off' })
    this.render()
  }

  async startVoice() {
    if (this.voice) return
    const why = await localSpeech()
    if (why) { this.voiceProblem = why; this.render(); return }
    this.voiceProblem = null
    this.voice = listen({
      onUtterance: (text, confidence) => { if (this.on) this.send(confidence == null ? { what: 'heard', text } : { what: 'heard', text, confidence }) },
      onProblem: (w) => { this.voiceProblem = w; this.voice = null; this.render() },
      onState: (on) => { this.listening = on; this.render() },
    })
  }

  stopVoice() { this.voice?.stop(); this.voice = null; this.listening = false }

  // MARK: what the Mac says

  receive(m) {
    if (!m || typeof m !== 'object') return
    if (m.what === 'list' || m.what === 'diff') {
      if (!this.on) return
      const next = applyHints(this.list, m)
      if (!next) { this.send(this.list?.zone ? { what: 'get', zone: this.list.zone } : { what: 'get' }); return }   // out of step: the whole list
      const was = this.list
      this.list = next
      if (this.patch(was, next)) return
    }
    else if (m.what === 'did') { this.ask = null; this.show(m.say) ; return }
    else if (m.what === 'ignored') { this.show(m.text); return }
    else if (m.what === 'ask') { this.ask = { id: m.ask, say: m.say || 'Do it?' } }
    else if (m.what === 'show') { this.turnOn(false); return }
    else if (m.what === 'off') { this.turnOff(false); return }
    else if (m.what === 'error') { this.show(m.error); return }
    this.render()
  }

  /// The labels moved, added and removed in place (the same badge nodes, their style changed) — no redraw. False when
  /// it can't be (no layer drawn yet, the zones changed): the caller draws again.
  patch(was, next) {
    if (!was || (was.zone || null) !== (next.zone || null) || JSON.stringify(was.zones || []) !== JSON.stringify(next.zones || [])) return false
    const layer = this.win.root?.querySelector?.('.hlayer')
    if (!layer) return false
    const shown = new Map()
    for (const el of layer.querySelectorAll('.hint')) shown.set(String(el.dataset?.act || '').slice(5), el)
    const want = new Map((next.hints || []).map((h) => [h.label, h]))
    for (const [label, el] of shown) if (!want.has(label)) el.remove()
    for (const h of next.hints || []) {
      const el = shown.get(h.label)
      if (!el) { layer.insertAdjacentHTML('beforeend', badge(h)); continue }
      el.style.left = pct(h.x); el.style.top = pct(h.y)
      if (h.title) el.dataset.help = h.title; else delete el.dataset.help
    }
    return true
  }

  show(words) {
    if (!words) return
    this.flash = words
    clearTimeout(this.flashTimer)
    this.flashTimer = setTimeout(() => { this.flash = null; if (this.on) this.render() }, 1800)
    this.render()
  }

  dispose() { clearTimeout(this.flashTimer); this.stopVoice() }
}
